import { useContext } from 'react'
import styled from 'styled-components'

import { Button } from '@syconium/little-miss-figgy/dist/components/Button'

import { VariantSelectionFormContext } from '../../../../app/_components/forms/VariantSelectionForm'
import { useVariantSelectionSizeField } from '../../../../app/_components/forms/VariantSelectionSizeField'
import { usePageTypeTracking } from '../../../../app/_hooks/useStaticTracking'
import {
  useCart,
  useCartDialog,
  useCartInterstitialDialog,
} from '../../../../app/_providers/CartProvider.client'
import { useFeatureFlag } from '../../../../app/_providers/ExperimentationProvider.client'
import { useTrackingLocation } from '../../../../app/_providers/TrackingLocationProvider.client'
import { type SignalTrackingLocation } from '../../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { useEmbroideryDialog } from '../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { useMakeEmbroideredCartItem } from '../../../../containers/EmbroideryContainer/useMakeEmbroideredCartItem'

export const EmbroideryAddToBag: React.FC = () => {
  const { addItems } = useCart()
  const makeEmbroideredCartItem = useMakeEmbroideredCartItem()
  const { product, variantGroup, selectedVariant } = useContext(VariantSelectionFormContext)!
  const { value: size } = useVariantSelectionSizeField()
  const { open: openCartDialog } = useCartDialog()
  const { open: openCartInterstitial } = useCartInterstitialDialog()
  const { confirmAndClose } = useEmbroideryDialog()
  const pageType = usePageTypeTracking()
  const trackingLocation = useTrackingLocation()

  const cartInterstitialTestDecision = useFeatureFlag<
    'no-cart-interstitial' | 'yes-cart-interstitial'
  >({
    key: 'cart-interstitial-test',
    controlVariant: 'no-cart-interstitial',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
  })

  const {
    embroidery: { addToBag: addToBagText, selectASize },
  } = useTranslation()

  const isSizeSelected = Boolean(size)
  const isAddToBagDisabled =
    !isSizeSelected || !selectedVariant || selectedVariant.availability !== 'AVAILABLE'

  const onClick = () => {
    if (!selectedVariant || !isSizeSelected) return
    const tracking: SignalTrackingLocation = {
      location: pageType,
      secondary_location: 'Embroidery Modal',
      section_number: trackingLocation.sectionNumber ?? null,
      position: trackingLocation.position ?? null,
      href: null,
      quickbuy: trackingLocation.isQuickbuy ?? false,
    }
    addItems(
      makeEmbroideredCartItem(
        {
          product,
          variant: selectedVariant,
          variantGroup,
          options: {
            quantity: 1,
            attributedPortal: undefined,
          },
        },
        variantGroup.primaryCategory.id
      ).map(cartItem => ({ ...cartItem, tracking }))
    )
    // TODO: Do we need to check for kits??
    confirmAndClose()
    if (cartInterstitialTestDecision === 'yes-cart-interstitial') {
      openCartInterstitial()
    } else {
      openCartDialog()
    }
  }

  return (
    <ATBButton
      data-testid='embroidery-action-button'
      aria-disabled={isAddToBagDisabled}
      disabled={isAddToBagDisabled}
      onClick={isAddToBagDisabled ? undefined : onClick}
    >
      {isAddToBagDisabled ? selectASize : addToBagText}
    </ATBButton>
  )
}

const ATBButton = styled(Button)`
  padding: ${o => o.theme.spacing(4, 8)};
  margin: ${o => o.theme.spacing(2.5, 0, 0, 0)};
  line-height: ${o => o.theme.spacing(4.5)};
  width: 100%;

  &[disabled],
  &[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.6;
  }
`
